import React, { useState } from 'react';

const CartSidebar = ({ isOpen, onClose, message, messages }) => {
  const [activeTab, setActiveTab] = useState('cart');

  const parseRawPayload = (rawPayload) => {
    if (!rawPayload) return null;

    if (typeof rawPayload === 'string') {
      try {
        return JSON.parse(rawPayload);
      } catch (e) {
        return null;
      }
    }

    return rawPayload;
  };

  const tryParseJsonish = (value) => {
    if (value == null) return null;
    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch (_) {
      // continue
    }

    // Some tool outputs look like Ruby hashes ({"key" => "value"}). Try a light conversion.
    try {
      return JSON.parse(value.replace(/=>/g, ':'));
    } catch (_) {
      return null;
    }
  };

  const formatJsonish = (value) => {
    const parsed = tryParseJsonish(value);
    if (parsed == null) return value;

    try {
      return JSON.stringify(parsed, null, 2);
    } catch (_) {
      return String(value);
    }
  };

  const getToolSourceMessage = () => {
    if (!message || !messages) return message;
    if (message.direction === 'bot') return message;

    const messageIndex = messages.findIndex(m => m.id === message.id);
    if (messageIndex === -1) return null;

    for (let i = messageIndex + 1; i < messages.length; i++) {
      if (messages[i].direction === 'bot') return messages[i];
      if (messages[i].direction === 'user') break;
    }

    return null;
  };

  const extractToolCalls = (rawPayload) => {
    const payload = parseRawPayload(rawPayload);
    const vapi = payload?.vapi;
    if (!vapi) return [];

    const output = Array.isArray(vapi.output) ? vapi.output : [];

    const toolCallOrder = [];
    const toolCallsById = {};
    const toolResultsById = {};

    output.forEach((msg) => {
      if (!msg || typeof msg !== 'object') return;

      if (msg.role === 'assistant' && Array.isArray(msg.tool_calls)) {
        msg.tool_calls.forEach((call) => {
          const id = call?.id;
          if (!id) return;

          if (!toolCallsById[id]) toolCallOrder.push(id);

          toolCallsById[id] = {
            id,
            type: call?.type,
            name: call?.function?.name,
            arguments: call?.function?.arguments
          };
        });
      }

      if (msg.role === 'tool' && msg.tool_call_id) {
        const id = msg.tool_call_id;
        if (!toolResultsById[id]) toolResultsById[id] = [];
        toolResultsById[id].push(msg.content);
      }
    });

    return toolCallOrder
      .map((id) => ({
        ...toolCallsById[id],
        results: toolResultsById[id] || []
      }))
      .filter((call) => call.name || call.id);
  };
  
  // Helper function to check if LLM data is available
  const hasLLMData = () => {
    if (!message) return false;
    
    // Check if message has its own LLM request
    if (message.llm_request) return true;
    
    // For bot messages, check if preceding user message has LLM request
    if (message.direction === 'bot' && messages) {
      const messageIndex = messages.findIndex(m => m.id === message.id);
      if (messageIndex > 0) {
        const prevMessage = messages[messageIndex - 1];
        if (prevMessage.direction === 'user' && prevMessage.llm_request) {
          return true;
        }
      }
    }
    
    return false;
  };

  const getCartData = () => {
    if (!message) return null;
    
    // For user messages, find the most recent bot message before this one
    if (message.direction === 'user') {
      const messageIndex = messages.findIndex(m => m.id === message.id);
      if (messageIndex === -1) return null;
      
      // Look backwards from this message to find the most recent bot message
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].direction === 'bot') {
          const botMessage = messages[i];
          try {
            const payload = parseRawPayload(botMessage.raw_payload);
            
            return payload.cart || payload.cartState || null;
          } catch (e) {
            console.error('Failed to parse cart data:', e);
            return null;
          }
        }
      }
      return null;
    }
    
    // For bot messages, use their own cart data
    try {
      const payload = parseRawPayload(message.raw_payload);
      
      return payload.cart || payload.cartState || null;
    } catch (e) {
      console.error('Failed to parse cart data:', e);
      return null;
    }
  };

  const formatPrice = (price) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const cartData = getCartData();
  const toolSourceMessage = getToolSourceMessage();
  const toolCalls = toolSourceMessage ? extractToolCalls(toolSourceMessage.raw_payload) : [];

  const TabButton = ({ name, label, isActive, icon }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`relative px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all duration-300 shrink-0 ${
        isActive 
          ? 'text-cyan-400' 
          : 'text-gray-500 hover:text-gray-300'
      }`}
    >
      <div className="flex items-center space-x-2">
        {icon}
        <span>{label}</span>
      </div>
      {isActive && (
        <>
          <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 h-1 bg-cyan-400/20 blur-sm"></div>
        </>
      )}
    </button>
  );

  const renderCartTab = () => (
    <div className="space-y-4">
      {!cartData ? (
        <div className="text-gray-500 text-center py-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-sm">No cart data available</p>
        </div>
      ) : (
        <>
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-white uppercase tracking-wider text-sm flex items-center space-x-2">
                <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <span>Cart Items</span>
              </h4>
              <span className="text-xs font-mono text-cyan-400 bg-cyan-900/30 px-2 py-1 rounded border border-cyan-800/50">
                {cartData.items?.length || 0} items
              </span>
            </div>
            
            {cartData.items && cartData.items.length > 0 ? (
              <div className="space-y-2">
                {cartData.items.map((item, index) => (
                  <div key={index} className="relative bg-slate-900/50 rounded-lg border border-slate-700 p-3 hover:border-cyan-800/50 transition-all duration-300 group">
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"></div>
                    <div className="relative flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-white">{item.name}</div>
                        {item.option && (
                          <div className="text-xs text-cyan-400 mt-1">
                            <span className="text-gray-500">Option:</span> {item.option}
                          </div>
                        )}
                        {item.addons && item.addons.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            <span className="text-gray-500">Add-ons:</span>
                            <div className="ml-2 mt-1 space-y-0.5">
                              {item.addons.map((addon, idx) => (
                                <div key={idx} className="flex items-center space-x-1">
                                  <span className="text-cyan-600">+</span>
                                  <span>{typeof addon === 'object' ? (addon.name || JSON.stringify(addon)) : addon}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-2 font-mono">
                          {item.quantity} × {formatPrice(item.price)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-cyan-400 font-mono">
                          {formatPrice(item.quantity * parseFloat(item.price))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm text-center py-4">No items in cart</div>
            )}
            
            {/* Combos Section */}
            {cartData.combos && cartData.combos.length > 0 && (
              <>
                <div className="flex items-center justify-between mt-6">
                  <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Combo Deals</span>
                  <span className="text-xs text-purple-500 font-mono">
                    {cartData.combos.length} combo{cartData.combos.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="space-y-2 mt-2">
                  {cartData.combos.map((combo, index) => (
                    <div key={index} className="relative bg-purple-900/20 rounded-lg border border-purple-700 p-3 hover:border-purple-600 transition-all duration-300 group">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"></div>
                      <div className="relative">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold text-white flex items-center gap-2">
                              <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                              {combo.name}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              {combo.quantity > 1 && (
                                <div className="text-xs text-purple-300">
                                  <svg className="inline w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                  </svg>
                                  Qty: {combo.quantity}
                                </div>
                              )}
                              {combo.items && combo.items.length > 0 && (
                                <div className="text-xs text-purple-300">
                                  <svg className="inline w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                  {combo.items.length} items
                                </div>
                              )}
                              {(() => {
                                const totalAddons = combo.items?.reduce((sum, item) => sum + (item.addons?.length || 0), 0) || 0;
                                return totalAddons > 0 ? (
                                  <div className="text-xs text-purple-300">
                                    <svg className="inline w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    {totalAddons} add-ons
                                  </div>
                                ) : null;
                              })()}
                            </div>
                            {combo.items && combo.items.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {combo.items.map((item, itemIndex) => (
                                  <div key={itemIndex} className="bg-purple-900/10 rounded-lg border border-purple-800/50 p-2.5">
                                    <div className="flex items-start gap-2">
                                      <div className="flex-shrink-0 mt-0.5">
                                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-white">{item.name}</span>
                                          {item.option && (
                                            <span className="text-xs px-1.5 py-0.5 bg-purple-800/50 text-purple-300 rounded border border-purple-700">
                                              {item.option}
                                            </span>
                                          )}
                                        </div>
                                        
                                        {/* Item details */}
                                        <div className="mt-1 text-xs text-gray-400 space-y-0.5">
                                          {item.quantity && item.quantity > 1 && (
                                            <div className="flex items-center gap-1">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                              </svg>
                                              <span>Quantity: {item.quantity}</span>
                                            </div>
                                          )}
                                          
                                          {item.price && (
                                            <div className="flex items-center gap-1">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                              <span>{formatPrice(item.price)}</span>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Addons */}
                                        {item.addons && item.addons.length > 0 && (
                                          <div className="mt-2 pl-3 border-l-2 border-purple-700/30">
                                            <div className="text-xs font-medium text-purple-400 mb-1">Add-ons:</div>
                                            <div className="space-y-0.5">
                                              {item.addons.map((addon, addonIndex) => (
                                                <div key={addonIndex} className="flex items-center gap-1 text-xs text-gray-300">
                                                  <svg className="w-3 h-3 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                                  </svg>
                                                  <span>{typeof addon === 'string' ? addon : (addon.name || JSON.stringify(addon))}</span>
                                                  {typeof addon === 'object' && addon.price && (
                                                    <span className="text-purple-400 ml-1">({formatPrice(addon.price)})</span>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Special requests */}
                                        {item.special_requests && (
                                          <div className="mt-2 text-xs text-amber-400 italic">
                                            <svg className="inline w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            {item.special_requests}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Item image if available */}
                                      {item.image_url && (
                                        <div className="flex-shrink-0">
                                          <img 
                                            src={item.image_url} 
                                            alt={item.name}
                                            className="w-12 h-12 rounded-lg object-cover border border-purple-700"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {(combo.base_price !== undefined && combo.base_price !== null) || combo.total_price ? (
                            <div className="text-right flex flex-col items-end">
                              <div className="font-bold text-purple-400 font-mono text-lg">
                                {formatPrice(combo.total_price || (combo.quantity * parseFloat(combo.base_price)))}
                              </div>
                              {combo.quantity > 1 && combo.base_price && (
                                <div className="text-xs text-purple-300 mt-0.5">
                                  {formatPrice(combo.base_price)} each
                                </div>
                              )}
                              {combo.savings && (
                                <div className="text-xs text-green-400 mt-1 font-medium">
                                  Save {formatPrice(combo.savings)}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {cartData.total !== undefined && (
            <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-lg border border-cyan-800/50 p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-white uppercase tracking-wider">Total</span>
                <span className="text-2xl font-bold text-cyan-400 font-mono">
                  {formatPrice(cartData.total)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderLLMTab = () => {
    // For bot messages, look for the LLM request from the preceding user message
    let llmRequest = message.llm_request;
    let associatedUserMessage = null;
    
    if (message.direction === 'bot' && !llmRequest) {
      const messageIndex = messages.findIndex(m => m.id === message.id);
      if (messageIndex > 0) {
        // Check the immediately preceding message
        const prevMessage = messages[messageIndex - 1];
        if (prevMessage.direction === 'user' && prevMessage.llm_request) {
          llmRequest = prevMessage.llm_request;
          associatedUserMessage = prevMessage;
        }
      }
    }
    
    return (
      <div className="space-y-4">
        {!llmRequest ? (
          <div className="text-gray-500 text-center py-8">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-sm">No LLM request data</p>
          </div>
        ) : (
          <>
            {associatedUserMessage && (
              <div className="bg-amber-900/20 border border-amber-800/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-400">
                  <span className="font-bold uppercase">Note:</span> This LLM request generated the user message that triggered this bot response
                </p>
              </div>
            )}
            
            <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-gray-500 uppercase">Provider</span>
                <span className="text-sm font-bold text-cyan-400">{llmRequest.provider}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Model</span>
              <span className="text-sm font-mono text-white">{llmRequest.model}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Duration</span>
              <span className="text-sm font-mono text-amber-400">{formatDuration(llmRequest.duration_ms)}</span>
            </div>
            
            <div className="pt-3 border-t border-slate-700 grid grid-cols-2 gap-3">
              <div className="bg-slate-900/50 rounded p-2 text-center">
                <div className="text-2xl font-bold text-cyan-400 font-mono">
                  {llmRequest.input_tokens || '-'}
                </div>
                <div className="text-xs text-gray-500 uppercase mt-1">Input Tokens</div>
              </div>
              <div className="bg-slate-900/50 rounded p-2 text-center">
                <div className="text-2xl font-bold text-emerald-400 font-mono">
                  {llmRequest.output_tokens || '-'}
                </div>
                <div className="text-xs text-gray-500 uppercase mt-1">Output Tokens</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-3 border-t border-slate-700">
              <span className="text-xs font-mono text-gray-500 uppercase">Status</span>
              <div className={`flex items-center space-x-2 ${llmRequest.success ? 'text-emerald-400' : 'text-red-400'}`}>
                {llmRequest.success ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="font-bold uppercase text-xs">{llmRequest.success ? 'Success' : 'Failed'}</span>
              </div>
            </div>
          </div>

          {llmRequest.error_message && (
            <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3">
              <h5 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-1">Error Message</h5>
              <p className="text-sm text-red-300 font-mono">{llmRequest.error_message}</p>
            </div>
          )}

          <a
            href={`/admin/llm_requests/${llmRequest.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-800/50 rounded-lg p-3 hover:from-cyan-900/30 hover:to-blue-900/30 transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-cyan-400 group-hover:text-cyan-300">View Full Request</span>
              <svg className="w-4 h-4 text-cyan-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </a>
        </>
      )}
    </div>
    );
  };

  const renderAPITab = () => (
    <div className="space-y-4">
      {!message.http_log ? (
        <div className="text-gray-500 text-center py-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">No API request data</p>
        </div>
      ) : (
        <>
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Method</span>
              <span className="font-bold text-amber-400 bg-amber-900/30 px-2 py-1 rounded text-xs">
                {message.http_log.request_method}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Status</span>
              <span className={`font-bold text-xs px-2 py-1 rounded ${
                message.http_log.response_status >= 200 && message.http_log.response_status < 300 
                  ? 'text-emerald-400 bg-emerald-900/30' 
                  : 'text-red-400 bg-red-900/30'
              }`}>
                {message.http_log.response_status}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Duration</span>
              <span className="text-sm font-mono text-cyan-400">{formatDuration(message.http_log.duration_ms)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Response Size</span>
              <span className="text-sm font-mono text-purple-400">
                {formatBytes(message.http_log.response_body?.length || 0)}
              </span>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
            <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Endpoint</h5>
            <div className="bg-slate-900/50 p-3 rounded font-mono text-xs text-cyan-400 break-all">
              {message.http_log.request_url}
            </div>
          </div>

          <a
            href={`/admin/http_logs/${message.http_log.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-800/50 rounded-lg p-3 hover:from-purple-900/30 hover:to-pink-900/30 transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-purple-400 group-hover:text-purple-300">View Full Request</span>
              <svg className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </a>
        </>
      )}
    </div>
  );

  const renderValidationTab = () => {
    const hasValidationData = message && (
      message.validation_score !== undefined ||
      message.validation_analysis ||
      message.validation_reasoning ||
      message.validation_passed !== undefined ||
      message.validation_full_response ||
      message.validation_llm_request_id
    );
    
    
    return (
      <div className="space-y-4">
        {!hasValidationData ? (
          <div className="text-gray-500 text-center py-8">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm">No validation data available</p>
          </div>
        ) : (
          <>
            {/* Validation Score and Status */}
            <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-3">
              {message.validation_score !== undefined && message.validation_score !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-500 uppercase">Validation Score</span>
                  <span className={`text-2xl font-bold font-mono ${
                    message.validation_score >= 0.7 ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {message.validation_score.toFixed(2)}
                  </span>
                </div>
              )}
              
              {message.validation_passed !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-500 uppercase">Status</span>
                  <div className={`flex items-center space-x-2 ${
                    message.validation_passed ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {message.validation_passed ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className="font-bold uppercase text-xs">
                      {message.validation_passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Validation Analysis */}
            {message.validation_analysis && (
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <h5 className="text-xs font-bold text-cyan-400 uppercase tracking-wider mb-2">Analysis</h5>
                <div className="text-sm text-gray-300 whitespace-pre-wrap">{message.validation_analysis}</div>
              </div>
            )}

            {/* Validation Reasoning */}
            {message.validation_reasoning && (
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <h5 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">Reasoning</h5>
                <div className="text-sm text-gray-300 whitespace-pre-wrap">{message.validation_reasoning}</div>
              </div>
            )}

            {/* Full Response (Collapsible) */}
            {message.validation_full_response && (
              <details className="bg-slate-800/50 rounded-lg border border-slate-700">
                <summary className="p-4 cursor-pointer hover:bg-slate-700/30 transition-colors">
                  <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Full LLM Response</span>
                </summary>
                <div className="px-4 pb-4">
                  <pre className="bg-slate-900/50 p-3 rounded font-mono text-xs text-gray-400 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                    {message.validation_full_response}
                  </pre>
                </div>
              </details>
            )}

            {/* Link to Validation LLM Request */}
            {message.validation_llm_request_id && (
              <a
                href={`/admin/llm_requests/${message.validation_llm_request_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-800/50 rounded-lg p-3 hover:from-purple-900/30 hover:to-pink-900/30 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-purple-400 group-hover:text-purple-300">View Validation LLM Request</span>
                  <svg className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </a>
            )}
          </>
        )}
      </div>
    );
  };

  const renderToolsTab = () => (
    <div className="space-y-4">
      {!toolSourceMessage ? (
        <div className="text-gray-500 text-center py-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm">No tool calls available</p>
          <p className="text-xs text-gray-600 mt-1">Select a bot message or the user message that triggered it</p>
        </div>
      ) : toolCalls.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm">No tool calls detected</p>
          <p className="text-xs text-gray-600 mt-1">Check the Raw tab for the full payload</p>
        </div>
      ) : (
        <>
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-gray-500 uppercase">Tool Calls</span>
              <span className="text-xs font-mono text-cyan-400">{toolCalls.length}</span>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              From bot message <span className="font-mono text-gray-500">{toolSourceMessage.id}</span>
            </div>
          </div>

          <div className="space-y-3">
            {toolCalls.map((call) => {
              const argsPreview = call.arguments
                ? String(call.arguments).replace(/\s+/g, ' ').slice(0, 140)
                : '';
              const summary = argsPreview ? `— ${argsPreview}` : '';

              return (
                <details key={call.id} className="bg-slate-800/50 rounded-lg border border-slate-700">
                  <summary className="p-4 cursor-pointer hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold text-cyan-400 shrink-0">{call.name || 'Tool Call'}</span>
                      <span className="text-xs font-mono text-gray-400 truncate">{summary}</span>
                    </div>
                  </summary>

                  <div className="px-4 pb-4 space-y-3">
                    <div className="text-xs font-mono text-gray-500">
                      <span className="uppercase">Tool Call ID</span>: {call.id}
                    </div>

                    {call.arguments && (
                      <div>
                        <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Arguments</h5>
                        <pre className="bg-slate-900/50 p-3 rounded font-mono text-xs text-gray-300 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                          {formatJsonish(call.arguments)}
                        </pre>
                      </div>
                    )}

                    <div>
                      <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Result</h5>
                      {call.results.length === 0 ? (
                        <div className="text-xs text-gray-500">No tool result payload captured</div>
                      ) : (
                        call.results.map((result, idx) => (
                          <pre
                            key={`${call.id}-${idx}`}
                            className="bg-slate-900/50 p-3 rounded font-mono text-xs text-emerald-300 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words mb-2 last:mb-0"
                          >
                            {formatJsonish(result)}
                          </pre>
                        ))
                      )}
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderRawDataTab = () => (
    <div className="space-y-4">
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
        <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Raw JSON Payload</h5>
        <pre className="bg-slate-900/50 p-3 rounded font-mono text-xs text-emerald-400 overflow-x-auto max-h-96 overflow-y-auto">
          {JSON.stringify(parseRawPayload(message.raw_payload) || message.raw_payload, null, 2)}
        </pre>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-slate-900 shadow-2xl overflow-hidden border-l border-slate-800">
      <div className="w-96 h-full flex flex-col">
        <div className="relative bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-transparent"></div>
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
          
          {/* Tech grid pattern overlay */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
          
          <div className="relative flex items-center justify-between p-5">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500 blur-lg opacity-20"></div>
                <svg className="relative w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span>Message Details</span>
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 border border-white/10 hover:border-white/20"
            >
              <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
        </div>

        {message && (
          <>
            <div className="bg-slate-800/50 px-5 py-4 border-b border-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    message.direction === 'user' 
                      ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50' 
                      : 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/50'
                  }`}>
                    {message.direction}
                  </span>
                  {message.latency_ms > 0 && (
                    <span className="text-xs font-mono text-amber-400 bg-amber-900/30 px-2 py-1 rounded border border-amber-800/50">
                      {formatDuration(message.latency_ms)}
                    </span>
                  )}
                </div>
                <span className="text-xs font-mono text-gray-500">
                  {new Date(message.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>

            <div className="bg-slate-800/30 border-b border-slate-800">
              <div className="flex overflow-x-auto">
                <TabButton 
                  name="cart" 
                  label="Cart" 
                  isActive={activeTab === 'cart'}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                />
                {hasLLMData() && message.direction === 'user' && (
                  <TabButton 
                    name="llm" 
                    label="LLM" 
                    isActive={activeTab === 'llm'}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    }
                  />
                )}
                <TabButton 
                  name="tools" 
                  label="Tools" 
                  isActive={activeTab === 'tools'}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  }
                />
                {message.http_log && (
                  <TabButton 
                    name="api" 
                    label="API" 
                    isActive={activeTab === 'api'}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    }
                  />
                )}
                {(message && message.direction === 'bot' && ((message.validation_score !== undefined && message.validation_score !== null) || message.validation_analysis || message.validation_reasoning || message.validation_passed !== undefined)) && (
                  <TabButton 
                    name="validation" 
                    label="Validation" 
                    isActive={activeTab === 'validation'}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                  />
                )}
                <TabButton 
                  name="raw" 
                  label="Raw" 
                  isActive={activeTab === 'raw'}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  }
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 text-gray-300">
              {activeTab === 'cart' && renderCartTab()}
              {activeTab === 'llm' && renderLLMTab()}
              {activeTab === 'tools' && renderToolsTab()}
              {activeTab === 'api' && renderAPITab()}
              {activeTab === 'validation' && renderValidationTab()}
              {activeTab === 'raw' && renderRawDataTab()}
            </div>
          </>
        )}

        {!message && (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <svg className="w-20 h-20 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm font-medium">No message selected</p>
              <p className="text-xs text-gray-600 mt-1">Click on a message to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartSidebar;
